import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import './ProjectView.css'
import {
  getProject,
  listMessages,
  sendMessage,
  sendChatMessage,
  uploadDataset,
  startWorkflow,
  getProjectMeta,
  saveProjectMeta,
  humanResolve,
  inspectDataset,
} from '../lib/api'
import { buildChatContext } from '../lib/buildChatContext'
import ColumnEditor from '../components/ColumnEditor'
import TableEditor from '../components/TableEditor'
import useWorkflowLive from '../lib/useWorkflowLive'
import FlowGraph from '../components/FlowGraph'
import StepDetails from '../components/StepDetails'
import HumanReviewCard from '../components/HumanReviewCard'
import ProjectSidebar from '../components/ProjectSidebar'
import ArtifactsView from '../components/ArtifactsView'
import InferenceView from '../components/InferenceView'
import LabelDataView from '../components/LabelDataView'
import MonitoringWrapper from './MonitoringWrapper'
import ModButton from '../ui/ModButton/ModButton'
import ModInputFile from '../ui/ModInputFile/ModInputFile'
import ModChatBubble from '../ui/ModChatBubble/ModChatBubble'
import ModTabs from '../ui/ModTabs/ModTabs'
import ModTabPanels from '../ui/ModTabs/ModTabPanels'
import ModTabPanel from '../ui/ModTabs/ModTabPanel'
import ModPanel from '../ui/ModPanel/ModPanel'
import { useLang } from '../lib/useLang'
import CrmImport from '../components/CrmImport'

export default function ProjectView() {
  const { t } = useLang()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [project, setProject] = useState(null)
  const [projectMeta, setProjectMeta] = useState(null)
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [fileInfo, setFileInfo] = useState(null)
  const [isMultipleUpload, setIsMultipleUpload] = useState(false)

  /**
   * tables - массив объектов, описывающих таблицы загруженного датасета.
   * Пример:
   * [
   *   {
   *     id: 0,
   *     name: 'my-data.csv',
   *     dataset_path: '/home/devops/storage/projects/1/my-data.csv',
   *     columns: [
   *       { name: 'column1', type: 'string', description: 'Description of column1' },
   *       { name: 'column2', type: 'int', description: 'Description of column2' },
   *     ]
   *   }
   * ]
   */
  const [tables, setTables] = useState([])
  const [relations, setRelations] = useState([])
  const [selectedTableId, setSelectedTableId] = useState(null)
  const [uploadingFiles, setUploadingFiles] = useState([]) // Files currently being uploaded
  const [uploadProgress, setUploadProgress] = useState({}) // Progress per file: { filename: 0-100 }

  // Log tables changes for debugging
  useEffect(() => {
    console.log('[ProjectView] tables state changed:', tables)
  }, [tables])

  // Auto-select first table when tables are loaded
  useEffect(() => {
    if (tables.length > 0 && selectedTableId === null) {
      setSelectedTableId(tables[0].id)
    }
  }, [tables, selectedTableId])

  const [target, setTarget] = useState('')
  const [includeMeta, setIncludeMeta] = useState(true)
  const [savingMeta, setSavingMeta] = useState(false)
  const bottomRef = useRef(null)
  const [startPending, setStartPending] = useState(false)
  const [launchHintOpen, setLaunchHintOpen] = useState(false)
  const [launchHintRect, setLaunchHintRect] = useState(null)
  const launchBtnRef = useRef(null)
  const [analysisProgress, setAnalysisProgress] = useState(0)
  const [tourOpen, setTourOpen] = useState(false)
  const [tourStep, setTourStep] = useState(0)
  const [tourRect, setTourRect] = useState(null)
  const [workflowId, setWorkflowId] = useState('')
  const [activeTab, setActiveTab] = useState('flow') // flow | details | timeline | artifacts
  const live = useWorkflowLive(workflowId)
  const [selectedStep, setSelectedStep] = useState('designer')
  const savedResultFor = useRef(null)
  const [graphOpen, setGraphOpen] = useState(false)
  const lastHumanKeyRef = useRef('')
  const lastQuestionsKeyRef = useRef('')
  const [resolvingHR, setResolvingHR] = useState(false)
  const [inspectOpen, setInspectOpen] = useState(false)
  const [inspectLoading, setInspectLoading] = useState(false)
  const [inspectReady, setInspectReady] = useState(false)
  const [inferenceMode, setInferenceMode] = useState(null) // null | 'batch' | 'online' | 'label'

  // Helper to update URL params without navigation
  const updateUrlParams = (tab, view) => {
    try {
      const u = new URL(window.location.href)
      if (tab) u.searchParams.set('tab', tab)
      else u.searchParams.delete('tab')
      if (view) u.searchParams.set('view', view)
      else u.searchParams.delete('view')
      window.history.replaceState({}, '', u.toString())
    } catch {}
  }
  const [dataTab, setDataTab] = useState('file') // 'file' | 'crm'
  // Label Data state (preserved across navigation)
  const [labelDataFiles, setLabelDataFiles] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`labelFiles_${id}`)
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [labelDataResult, setLabelDataResult] = useState(null)
  const [labelDataStatus, setLabelDataStatus] = useState('idle')
  const [labelDataError, setLabelDataError] = useState(null)
  const [labelDataProgress, setLabelDataProgress] = useState('')
  const [labelDataWorkflowInfo, setLabelDataWorkflowInfo] = useState(null)

  // Persist label data files to sessionStorage
  useEffect(() => {
    try {
      if (labelDataFiles.length > 0) sessionStorage.setItem(`labelFiles_${id}`, JSON.stringify(labelDataFiles))
      else sessionStorage.removeItem(`labelFiles_${id}`)
    } catch {}
  }, [labelDataFiles, id])
  const sendingRef = useRef(false)
  const processedFilesRef = useRef(new Set()) // Track processed files by name+size to avoid duplicates
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage?.getItem('sidebar_collapsed') === '1'
    } catch {
      return false
    }
  })
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev
      try {
        window.localStorage?.setItem('sidebar_collapsed', next ? '1' : '0')
      } catch {}
      return next
    })
  }
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try {
      return window.localStorage?.getItem('chat_collapsed') === '1'
    } catch {
      return false
    }
  })
  const toggleChat = () => {
    setChatCollapsed(prev => {
      const next = !prev
      try {
        window.localStorage?.setItem('chat_collapsed', next ? '1' : '0')
      } catch {}
      return next
    })
  }
  // Memoized summaries for Flow tooltips (must be at top level, not conditional)
  const flowSummaries = useMemo(() => {
    try {
      const s = {}
      const p = live?.snapshot?.partials || {}
      const getNotes = x => {
        try {
          const es = x?.execution_summary
          const n = es?.notes
          return typeof n === 'string' && n.trim() ? n : ''
        } catch {
          return ''
        }
      }
      s['demand'] = getNotes(p?.demand)
      s['analyst'] = getNotes(p?.analyst)
      s['designer'] = getNotes(p?.designer)
      try {
        const hr = live?.snapshot?.pending_human_request
        if (hr?.request?.message) s['human_review'] = hr.request.message
      } catch {}
      return s
    } catch {
      return {}
    }
  }, [live?.snapshot])

  useEffect(() => {
    if (live?.currentStep) setSelectedStep(live.currentStep)
  }, [live?.currentStep])

  // Emit system message when a human_request appears
  useEffect(() => {
    const hr = live?.snapshot?.pending_human_request
    if (!hr) return
    // Не допускаем, чтобы события от старого workflow попадали в новый проект
    try {
      if (!workflowId || (live?.snapshot?.run_meta?.workflow_id && live.snapshot.run_meta.workflow_id !== workflowId))
        return
    } catch {}
    try {
      const agent = hr.agent || 'agent'
      const req = hr.request || {}
      const msg = String(req.message || '').trim()
      const fields = Array.isArray(req.fields_needed) ? req.fields_needed : []
      const key = `${agent}|${msg}|${fields.join(',')}`
      // Persist dedup key in localStorage (keyed by workflow) so the same
      // human_review message is not re-inserted into project_messages on
      // remount/reload/SSE-reconnect — mirrors the demand-questions block.
      // The in-memory ref alone resets on remount and caused duplicates.
      const storeKey = `wfhr:${workflowId}`
      const last = typeof window !== 'undefined' ? window.localStorage?.getItem(storeKey) : null
      if (key && key !== last && key !== lastHumanKeyRef.current) {
        lastHumanKeyRef.current = key
        if (typeof window !== 'undefined') {
          try {
            window.localStorage?.setItem(storeKey, key)
          } catch {}
        }
        const text = t('projectView.humanReviewMsg', { agent, message: msg }) + (fields.length ? t('projectView.fieldsNeededMsg', { fields: fields.join(', ') }) : '')
        ;(async () => {
          try {
            const m = await sendMessage(id, 'system', text)
            setMessages(prev => [...prev, m])
          } catch {}
        })()
      }
    } catch {}
  }, [id, workflowId, live?.snapshot?.pending_human_request])

  // Emit system message with Demand questions when present
  useEffect(() => {
    try {
      const qs = live?.snapshot?.partials?.demand?.questions
      const hasHR = !!live?.snapshot?.pending_human_request
      // Не допускаем запись сообщений от старого workflow в новый проект
      if (!workflowId || (live?.snapshot?.run_meta?.workflow_id && live.snapshot.run_meta.workflow_id !== workflowId))
        return
      if (!Array.isArray(qs) || qs.length === 0 || !workflowId || !hasHR) return
      const key = JSON.stringify(qs)
      const storeKey = `wfqs:${workflowId}`
      const last = typeof window !== 'undefined' ? window.localStorage?.getItem(storeKey) : null
      if (key !== last && key !== lastQuestionsKeyRef.current) {
        lastQuestionsKeyRef.current = key
        if (typeof window !== 'undefined') {
          try {
            window.localStorage?.setItem(storeKey, key)
          } catch {}
        }
        const text = `${t('projectView.demandQuestions')}\n- ${qs.join('\n- ')}`
        ;(async () => {
          try {
            const m = await sendMessage(id, 'system', text)
            setMessages(prev => [...prev, m])
          } catch {}
        })()
      }
    } catch {}
  }, [id, workflowId, live?.snapshot?.partials?.demand?.questions, live?.snapshot?.pending_human_request])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      // Prevent показ старого flow при переключении проекта
      setWorkflowId('')
      setInferenceMode(null)
      try {
        // Clear file info immediately to avoid showing stale path during project switch
        setFileInfo(null)
        // Clear processed files ref when switching projects
        processedFilesRef.current.clear()
        const p = await getProject(id)
        setProject(p)
        const msgs = await listMessages(id)
        setMessages(msgs)
        const meta = await getProjectMeta(id)
        setProjectMeta(meta || {})
        setTarget(meta?.target || '')
        setIncludeMeta(meta?.include_meta ?? true)
        // Load tables from meta, or convert old columns_rows format to tables format
        let loadedTables = []
        if (Array.isArray(meta?.tables) && meta.tables.length > 0) {
          loadedTables = meta.tables
          setTables(loadedTables)
        } else if (Array.isArray(meta?.last_input?.tables) && meta.last_input.tables.length > 0) {
          // Convert last_input.tables format to tables format (restore from workflow input)
          loadedTables = meta.last_input.tables.map((table, idx) => ({
            id: idx,
            name: table.path ? table.path.split(/[/\\]/).pop() : `table_${idx}.csv`,
            dataset_path: table.path || '',
            columns: table.columns
              ? Object.entries(table.columns).map(([name, col]) => ({
                  name,
                  type: col.type || 'string',
                  description: col.description || '',
                }))
              : [],
          }))
          setTables(loadedTables)
        } else if (Array.isArray(meta?.columns_rows) && meta.columns_rows.length > 0) {
          // Convert old format to new format: create table with id:0
          const datasetPath = meta?.last_input?.dataset_path || ''
          const fileName = datasetPath ? datasetPath.split(/[/\\]/).pop() : 'dataset.csv'
          loadedTables = [{ id: 0, name: fileName, dataset_path: datasetPath, columns: meta.columns_rows }]
          setTables(loadedTables)
        } else {
          setTables([])
        }
        // Mark loaded tables as processed (we don't have file size, so use name only)
        // This prevents re-processing files that were already loaded from meta
        loadedTables.forEach(table => {
          if (table.name) {
            // Use a placeholder size since we don't have the original file size
            // This will prevent exact duplicates, but files with same name will be skipped
            processedFilesRef.current.add(`${table.name}:0`)
          }
        })
        // Restore last uploaded dataset path for this project from localStorage
        try {
          const key = `ds:${id}`
          const lp = typeof window !== 'undefined' ? window.localStorage?.getItem(key) : ''
          if (lp && !fileInfo?.dataset_path) setFileInfo({ dataset_path: lp })
        } catch {}
        // Restore active workflow if present, else prefer last completed; fallback to last "Workflow started" message
        const activeWid = meta?.active_workflow_id || ''
        const lastWid = meta?.last_workflow_id || ''
        // Preserve tab from URL if set (e.g. ?tab=inference)
        const urlTab = new URLSearchParams(window.location.search).get('tab')
        const shouldKeepTab = ['artifacts', 'inference', 'monitoring'].includes(urlTab)
        if (activeWid) {
          setWorkflowId(activeWid)
          if (!shouldKeepTab) setActiveTab('flow')
        } else if (lastWid) {
          setWorkflowId(lastWid)
          if (!shouldKeepTab) setActiveTab('flow')
        } else {
          try {
            const found = [...msgs]
              .reverse()
              .find(
                m => m.role === 'system' && typeof m.content === 'string' && m.content.startsWith('Workflow started: ')
              )
            const wid = found?.content?.split('Workflow started: ')[1]?.trim()
            if (wid) {
              setWorkflowId(wid)
              if (!shouldKeepTab) setActiveTab('flow')
            } else {
              setWorkflowId('')
            }
          } catch {
            setWorkflowId('')
          }
        }
      } catch (e) {
        navigate('/app', { replace: true })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, navigate])

  // Initialize tab and view from query (?tab=artifacts|inference|monitoring&view=...)
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || '')
      const tab = sp.get('tab')
      const view = sp.get('view')
      if (['artifacts', 'inference', 'monitoring'].includes(tab)) {
        setActiveTab(tab)
      }
      // Restore sub-view state
      if (tab === 'inference' && ['label', 'batch', 'online'].includes(view)) {
        setInferenceMode(view)
      }
      if (tab === 'flow' && view === 'details') {
        setActiveTab('details')
      }
      if (view === 'details' && !tab) {
        setActiveTab('details')
      }
    } catch {}
  }, [location.search])

  // При смене проекта по умолчанию возвращаем вкладку Flow
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || '')
      const tab = sp.get('tab')
      if (!tab) setActiveTab('flow')
    } catch {
      setActiveTab('flow')
    }
  }, [id])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, chatLoading])

  // Guided tour: recompute the active target rect whenever step changes,
  // window resizes, or the page scrolls. We use viewport (fixed) coords.
  useEffect(() => {
    if (!tourOpen) return
    const keys = ['upload', 'target', 'description', 'launch']
    const update = () => {
      const el = document.querySelector(`[data-tour="${keys[tourStep]}"]`)
      if (!el) {
        // Fallback: virtual centered rect so the tour card still renders without a halo.
        const cw = 280, ch = 172
        setTourRect({
          left: window.innerWidth / 2 - cw / 2,
          top: window.innerHeight / 2 - ch / 2,
          width: cw,
          height: ch,
          _virtual: true,
        })
        return
      }
      const r = el.getBoundingClientRect()
      setTourRect({ left: r.left, top: r.top, width: r.width, height: r.height })
    }
    update()
    const onEsc = e => { if (e.key === 'Escape') setTourOpen(false) }
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    window.addEventListener('keydown', onEsc)
    const id = setInterval(update, 400) // catches unrelated layout shifts
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('keydown', onEsc)
      clearInterval(id)
    }
  }, [tourOpen, tourStep])

  // Dataset-analysis ring animation: ease 0 → 95 while loading,
  // snap to 100 when the backend finishes, reset on close.
  useEffect(() => {
    if (!inspectOpen) {
      setAnalysisProgress(0)
      return
    }
    if (!inspectLoading) {
      setAnalysisProgress(100)
      return
    }
    let rafId
    let startTs = null
    const duration = 3200
    const tick = now => {
      if (startTs == null) startTs = now
      const p = Math.min((now - startTs) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 2.5)
      setAnalysisProgress(eased * 95)
      if (p < 1) rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [inspectOpen, inspectLoading])

  // Reset UI tabs/selection whenever workflowId changes or becomes empty
  useEffect(() => {
    // Do not override tab if explicitly selected via URL or sidebar
    const urlTab = new URLSearchParams(window.location.search).get('tab')
    const keepTab = ['artifacts', 'inference', 'monitoring'].includes(activeTab) ||
                    ['artifacts', 'inference', 'monitoring'].includes(urlTab)
    if (!keepTab) setActiveTab('flow')
    setSelectedStep('designer')
  }, [workflowId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSend = async () => {
    const msg = text.trim()
    if (!msg || sendingRef.current || chatLoading) return

    // If workflow is active — send to AI chat assistant
    if (workflowId) {
      setChatLoading(true)
      setText('')
      // Show user message immediately, before waiting for AI
      const tempId = `temp-${Date.now()}`
      setMessages(prev => [...prev, { id: tempId, role: 'user', content: msg, t: new Date().toISOString() }])
      try {
        // Always fetch monitoring data for completed workflows so AI can answer
        // monitoring questions from any tab (chat is hidden on monitoring tab itself)
        let monitoringData = null
        if (projectMeta?.last_model) {
          try {
            const latestRes = await fetch(`/api/monitoring/workflows/${workflowId}/latest`, { credentials: 'include' })
            if (latestRes.ok) {
              const job = await latestRes.json()
              if (job?.job_id && job?.status === 'completed') {
                const [metricsRes, driftRes] = await Promise.all([
                  fetch(`/api/monitoring/jobs/${job.job_id}/metrics`, { credentials: 'include' }),
                  fetch(`/api/monitoring/jobs/${job.job_id}/drift-info`, { credentials: 'include' }),
                ])
                monitoringData = {
                  metrics: metricsRes.ok ? await metricsRes.json() : null,
                  drift: driftRes.ok ? await driftRes.json() : null,
                }
              }
            }
          } catch { /* monitoring data is optional — proceed without it */ }
        }
        const context = buildChatContext(live, project, activeTab, tables, projectMeta, monitoringData)
        const { userMessage, assistantMessage } = await sendChatMessage(id, msg, context)
        // Update temp message in place (keeps same key → no unmount/remount → no fade flicker)
        // then append AI response as a new element
        setMessages(prev => {
          const msgs = prev.map(m => m.id === tempId ? { ...userMessage, id: tempId } : m)
          return [...msgs, assistantMessage]
        })
      } catch (e) {
        setMessages(prev => prev.filter(m => m.id !== tempId))
        setText(msg)
        setErr(e?.response?.data?.error || e?.message || t('projectView.failedSendMessage'))
      } finally {
        setChatLoading(false)
      }
      return
    }

    // No workflow yet — just store the message (used as prompt on workflow start)
    sendingRef.current = true
    setText('')
    try {
      const m = await sendMessage(id, 'user', msg)
      setMessages(prev => {
        const exists = prev.some(msg => msg.id === m.id)
        if (exists) return prev
        return [...prev, m]
      })
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('projectView.failedSendMessage'))
    } finally {
      sendingRef.current = false
    }
  }

  const onUpload = async e => {
    console.log('[ProjectView] onUpload called, files count:', e.target.files?.length || 0, 'current tables:', tables)
    const files = e.target.files
    const fileArray = files ? Array.from(files) : []
    setUploadError('')

    try {
      // Handle file removal: if selected files list is empty
      if (fileArray.length === 0) {
        // Don't clear tables — existing files are managed via onRemoveExisting,
        // and newly-uploaded files are handled by the removal logic below
        console.log('[ProjectView] onUpload: selected files empty, skipping')
        setFileInfo(null)
        setIsMultipleUpload(false)
        return
      }

      // Get current file keys
      const currentFileKeys = new Set(fileArray.map(file => `${file.name}:${file.size}`))

      // Find files that were removed (in processedFilesRef but not in current list)
      // Skip existing files (key ends with :0) — those are managed via onRemoveExisting
      const removedFileKeys = []
      processedFilesRef.current.forEach(fileKey => {
        if (!currentFileKeys.has(fileKey) && !fileKey.endsWith(':0')) {
          removedFileKeys.push(fileKey)
        }
      })

      // Remove tables for deleted files
      if (removedFileKeys.length > 0) {
        console.log('[ProjectView] onUpload: removing', removedFileKeys.length, 'deleted files')
        setTables(prev => {
          const updated = prev.filter(table => {
            // Check if this table corresponds to a removed file
            // We need to match by file name (table.name should match file name)
            const tableFileName = table.name || ''
            const shouldRemove = removedFileKeys.some(fileKey => {
              const fileName = fileKey.split(':')[0] // Extract file name from key
              return fileName === tableFileName
            })
            return !shouldRemove
          })
          console.log('[ProjectView] onUpload: tables after removal:', updated)
          return updated
        })

        // Remove from processedFilesRef
        removedFileKeys.forEach(fileKey => {
          processedFilesRef.current.delete(fileKey)
        })
      }

      // Filter out files that have already been processed (check by name + size, same as ModInputFile)
      const newFiles = fileArray.filter(file => {
        const fileKey = `${file.name}:${file.size}`
        return !processedFilesRef.current.has(fileKey)
      })

      // If there are new files to upload, process them
      if (newFiles.length > 0) {
        console.log(
          '[ProjectView] onUpload: processing',
          newFiles.length,
          'new files out of',
          fileArray.length,
          'total'
        )

        // Determine if multiple files are being uploaded
        const isMultiple = fileArray.length > 1
        setIsMultipleUpload(isMultiple)

        // Show uploading state immediately
        setUploadingFiles(newFiles.map(f => ({ name: f.name, size: f.size })))
        setUploadProgress({}) // Reset progress

        // Yield to browser so React renders the uploading UI before network starts
        await new Promise(r => setTimeout(r, 0))

        // Upload files sequentially to avoid server overload
        // (parallel uploads of large files can cause 502 errors)
        const uploadResults = []
        for (const file of newFiles) {
          const onProgress = progress => {
            setUploadProgress(prev => ({ ...prev, [file.name]: progress }))
          }
          const res = await uploadDataset(id, file, onProgress)
          // Mark file as processed
          const fileKey = `${file.name}:${file.size}`
          processedFilesRef.current.add(fileKey)
          uploadResults.push({ file, res })
        }

        // Set fileInfo to the last uploaded file (for backward compatibility)
        if (uploadResults.length > 0) {
          const lastResult = uploadResults[uploadResults.length - 1]
          setFileInfo(lastResult.res)
          // Persist last input path per project (localStorage) to survive navigation
          try {
            if (typeof window !== 'undefined')
              window.localStorage?.setItem(`ds:${id}`, String(lastResult.res?.dataset_path || ''))
          } catch {}
        }

        // Show inspect modal immediately after upload completes
        setInspectOpen(true)
        setInspectLoading(true)
        setInspectReady(false)

        // Auto-inspect columns and dtypes for all uploaded files
        try {

          // Helper function to map pandas dtypes to UI types
          const mapDtype = d => {
            const s = String(d || '').toLowerCase()
            if (s.startsWith('int') || s.startsWith('uint') || s.includes(' int') || s.includes('uint')) return 'int'
            if (s.includes('float') || s.includes('double')) return 'float'
            if (s === 'bool' || s.includes('bool')) return 'bool'
            if (
              s.includes('datetime') ||
              s.includes('date') ||
              s.includes('timestamp') ||
              s.includes('period') ||
              s.includes('timedelta') ||
              s === 'time'
            )
              return 'date'
            if (s.includes('category') || s.includes('categor')) return 'categorical'
            if (
              s.includes('string') ||
              s === 'object' ||
              s.includes('bytes') ||
              s.includes('complex') ||
              s.includes('mixed')
            )
              return 'string'
            return 'string'
          }

          // Inspect files sequentially to avoid sandbox overload
          const inspectionResults = []
          for (const { file, res } of uploadResults) {
            const p = res?.dataset_path || ''
            const fileName = file.name || (p ? p.split(/[/\\]/).pop() : 'dataset.csv') || 'dataset.csv'

            if (!p) {
              inspectionResults.push({ fileName, columns: [] })
              continue
            }

            try {
              const insp = await inspectDataset(p)
              console.log('[ProjectView] inspectDataset result for', fileName, ':', insp)
              const cols = Array.isArray(insp?.columns) ? insp.columns : []
              console.log('[ProjectView] parsed columns:', cols)
              const columns = cols.map(c => ({
                name: String(c.name || ''),
                type: mapDtype(c.dtype),
                description: '',
              }))
              console.log('[ProjectView] mapped columns:', columns)
              const datasetPath = insp?.fixed_path || p
              if (insp?.fixed) {
                console.log('[ProjectView] dataset was auto-fixed:', insp.fixed_path)
              }
              inspectionResults.push({ fileName, columns, dataset_path: datasetPath })
            } catch (inspErr) {
              console.error('[ProjectView] inspectDataset error for', fileName, ':', inspErr)
              setUploadError(t('projectView.failedInspect', { filename: fileName }))
              // Don't add failed file to results — it will be removed from tables
              processedFilesRef.current.forEach(key => {
                if (key.startsWith(fileName + ':')) processedFilesRef.current.delete(key)
              })
            }
          }

          // Add tables with inspected columns (replace existing tables with same name)
          setTables(prev => {
            const newNames = new Set(inspectionResults.map(r => r.fileName))
            const filtered = prev.filter(t => !newNames.has(t.name))
            let maxId = filtered.length > 0 ? Math.max(...filtered.map(t => (typeof t.id === 'number' ? t.id : 0)), -1) : -1

            const newTables = inspectionResults.map(({ fileName, columns, dataset_path }) => {
              maxId += 1
              return { id: maxId, name: fileName, dataset_path: dataset_path || '', columns }
            })

            const updated = [...filtered, ...newTables]
            console.log('[ProjectView] tables updated after inspection:', updated)
            return updated
          })

          setInspectLoading(false)
          setInspectReady(true)
        } catch (inspectErr) {
          console.error('[ProjectView] inspection error:', inspectErr)
          // Fallback: create tables without columns (replace existing tables with same name)
          setTables(prev => {
            const newNames = new Set(uploadResults.map(({ file }) => file.name))
            const filtered = prev.filter(t => !newNames.has(t.name))
            let maxId = filtered.length > 0 ? Math.max(...filtered.map(t => (typeof t.id === 'number' ? t.id : 0)), -1) : -1

            const newTables = uploadResults.map(({ file, res }) => {
              const p = res?.dataset_path || ''
              const fileName = file.name || (p ? p.split(/[/\\]/).pop() : 'dataset.csv') || 'dataset.csv'
              maxId += 1
              return { id: maxId, name: fileName, dataset_path: p, columns: [] }
            })

            const updated = [...filtered, ...newTables]
            return updated
          })
          setInspectLoading(false)
          setInspectReady(true)
        }

        // Clear uploading state
        setUploadingFiles([])
        setUploadProgress({})
      } else {
        // No new files, but files might have been removed
        // Update fileInfo to the last remaining file if any
        if (fileArray.length > 0) {
          // Find the last file in the current list and try to get its info
          // Since we don't have the upload result, we'll need to find matching table
          const lastFile = fileArray[fileArray.length - 1]
          const lastFileKey = `${lastFile.name}:${lastFile.size}`
          // If this file was processed before, we can find its table and use that info
          // For now, just update isMultipleUpload
          const isMultiple = fileArray.length > 1
          setIsMultipleUpload(isMultiple)
        }
      }
    } catch (e) {
      const status = e?.response?.status || e?.status
      const detail = e?.response?.data?.detail || e?.response?.data?.error
      const msg = detail || (status ? t('projectView.failedUploadStatus', { status }) : t('projectView.failedUpload'))
      setUploadError(msg)
      setUploadingFiles([])
      setUploadProgress({})
      // Remove failed files from tables so user can re-upload
      const failedNames = new Set(fileArray.map(f => f.name))
      setTables(prev => prev.filter(t => !failedNames.has(t.name)))
      failedNames.forEach(name => {
        processedFilesRef.current.forEach(key => {
          if (key.startsWith(name + ':')) processedFilesRef.current.delete(key)
        })
      })
    }
  }

  const onSendAndStart = async () => {
    // Collect all dataset paths from tables
    const datasetPaths = tables.map(table => table.dataset_path).filter(path => path && path.trim())

    if (datasetPaths.length === 0 || !text.trim()) {
      setErr(t('projectView.fileAndPromptRequired'))
      return
    }

    // Build tables payload with dataset_path and columns_schema for each table
    const tablesPayload = tables
      .filter(table => table.dataset_path && table.dataset_path.trim())
      .map(table => {
        // Build columns_schema for this specific table
        const tableSchema = {}
        if (Array.isArray(table.columns)) {
          for (const col of table.columns) {
            const name = (col.name || '').trim()
            if (!name) continue
            const entry = { type: (col.type || 'string').trim() }
            if (col.description && col.description.trim()) entry.description = col.description.trim()
            tableSchema[name] = entry
          }
        }
        return {
          path: table.dataset_path,
          columns: Object.keys(tableSchema).length > 0 ? tableSchema : null,
          role: undefined,
        }
      })

    // Transform UI relations to backend RelationInfo format
    const relationsPayload = relations
      .filter(rel => rel.leftTableJoiningColumn && rel.rightTableJoiningColumn)
      .map(rel => ({
        left_table: tables.find(t => String(t.id) === String(rel.leftTableId))?.dataset_path,
        right_table: tables.find(t => String(t.id) === String(rel.rightTableId))?.dataset_path,
        relation_type: rel.relationTypeId || 'auto',
        left_key: rel.leftTableJoiningColumn,
        right_key: rel.rightTableJoiningColumn,
      }))
      .filter(rel => rel.left_table && rel.right_table)

    const prompt = includeMeta ? buildPrompt(text, target) : text.trim()
    try {
      setStartPending(true)
      // Debug payload in console
      try {
        console.log('[ui:startWorkflow]', {
          tables: tablesPayload,
          relations: relationsPayload,
          user_prompt: prompt,
        })
      } catch {}
      // Post the same text that is sent to the orchestrator to the chat
      const m = await sendMessage(id, 'user', prompt)
      setMessages(prev => [...prev, m])
      const res = await startWorkflow(id, {
        tables: tablesPayload,
        relations: relationsPayload.length > 0 ? relationsPayload : undefined,
        user_prompt: prompt,
      })
      try {
        console.log('[ui:startWorkflow:response]', res)
      } catch {}
      if (res?.workflow_id) {
        // Set workflowId immediately for UI
        setWorkflowId(String(res.workflow_id))
        setActiveTab('flow')
        try {
          window.localStorage?.setItem(`wf:${id}`, res.workflow_id)
        } catch {}
        try {
          const sys = await sendMessage(id, 'system', `Workflow started: ${res.workflow_id}`)
          setMessages(prev => [...prev, sys])
        } catch {}
        // Persist active workflow in project meta
        try {
          await saveProjectMeta(id, {
            active_workflow_id: res.workflow_id,
            last_input: { tables: tablesPayload },
          })
          const meta = await getProjectMeta(id)
          setProjectMeta(meta || {})
        } catch {}
      } else {
        // Fallback: try parse from last system message
        try {
          const m2 = await listMessages(id)
          setMessages(m2)
          const found = [...m2]
            .reverse()
            .find(
              m => m.role === 'system' && typeof m.content === 'string' && m.content.startsWith('Workflow started: ')
            )
          const wid = found?.content?.split('Workflow started: ')[1]?.trim()
          if (wid) {
            setWorkflowId(wid)
            setActiveTab('flow')
            window.localStorage?.setItem(`wf:${id}`, wid)
          }
        } catch {}
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('projectView.failedStartWorkflow'))
    } finally {
      setStartPending(false)
    }
  }

  // Save final artifacts/metrics to project meta once per workflow
  useEffect(() => {
    if (!workflowId || !live?.status?.done) return
    if (savedResultFor.current === workflowId) return
    // Сохраняем результат только если workflow относится к текущему проекту
    const belongsHere = (() => {
      try {
        if (projectMeta?.active_workflow_id && projectMeta.active_workflow_id === workflowId) return true
        if (projectMeta?.last_workflow_id && projectMeta.last_workflow_id === workflowId) return true
      } catch {}
      return false
    })()
    if (!belongsHere) return
    const uri = live?.status?.artifacts?.model_uri
    // Prefer flattened metrics in partials.metrics; fallback to designer models[0]
    const flat = live?.snapshot?.partials?.metrics
    let metrics = flat || {}
    if (!Object.keys(metrics).length) {
      const first = live?.snapshot?.partials?.designer?.models?.[0]
      if (first?.metrics) {
        const m = {}
        for (const mm of first.metrics) if (mm.metric_name) m[mm.metric_name] = mm.metric_value
        metrics = m
      }
    }
    ;(async () => {
      try {
        await saveProjectMeta(id, {
          last_model: { uri, metrics, workflow_id: workflowId, finished_at: new Date().toISOString() },
          last_workflow_id: workflowId,
          active_workflow_id: null,
        })
        const meta = await getProjectMeta(id)
        setProjectMeta(meta || {})
        savedResultFor.current = workflowId
        try {
          window.localStorage?.removeItem(`wf:${id}`)
        } catch {}
      } catch {}
    })()
  }, [id, projectMeta?.active_workflow_id, projectMeta?.last_workflow_id, workflowId, live?.status?.done])

  // Autosave meta (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setSavingMeta(true)
        await saveProjectMeta(id, { target, include_meta: includeMeta, tables: tables })
      } catch {
      } finally {
        setSavingMeta(false)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [id, target, includeMeta, tables])

  const columns_schema = useMemo(() => {
    const out = {}
    for (const table of tables) {
      if (!Array.isArray(table.columns)) continue
      for (const r of table.columns) {
        const name = (r.name || '').trim()
        if (!name) continue
        const entry = { type: (r.type || 'string').trim() }
        if (r.description && r.description.trim()) entry.description = r.description.trim()
        out[name] = entry
      }
    }
    return out
  }, [tables])

  const buildPrompt = (base, tgt) => {
    let msg = base.trim()
    if (tgt) msg += `\n\n${t('projectView.targetVar')} ${tgt}`
    if (tables && tables.length > 0) {
      msg += `\n\n${t('projectView.columnDescriptions')}\n`
      for (const table of tables) {
        if (!Array.isArray(table.columns) || table.columns.length === 0) continue
        if (tables.length > 1) {
          msg += `\n${t('projectView.tableLabel')} ${table.name}\n`
        }
        for (const r of table.columns) {
          const name = (r.name || '').trim()
          if (!name) continue
          const type = (r.type || 'string').trim()
          const description = (r.description || '').trim()
          let line = `- ${name}`
          if (type) line += `: ${type}`
          if (description) line += ` — ${description}`
          msg += line + '\n'
        }
      }
    }
    return msg
  }

  if (loading)
    return (
      <div className='flex min-h-screen items-center justify-center bg-background'>
        <span className='loading loading-spinner loading-lg' />
      </div>
    )

  // Проект считается "с результатом", если:
  // - текущий live‑флоу завершён,
  // - или в метаданных есть сохранённая модель,
  // - или зафиксирован завершённый флоу (last_workflow_id)
  const hasCompleted = !!(
    live?.status?.done ||
    projectMeta?.last_model ||
    projectMeta?.last_workflow_id ||
    live?.status?.artifacts?.model_uri
  )
  const gridTemplate = (() => {
    if (activeTab === 'artifacts' || activeTab === 'monitoring') {
      return sidebarCollapsed ? '5% 95% 0%' : '20% 80% 0%'
    }
    // Chat collapsed: 44px slim sidebar on the right
    if (chatCollapsed) {
      return sidebarCollapsed ? '5% calc(95% - 44px) 44px' : '20% calc(80% - 44px) 44px'
    }
    return sidebarCollapsed ? '5% 57.5% 37.5%' : '20% 50% 30%'
  })()

  return (
    <div
      className='project-view-container relative h-[calc(100vh-76px-1px)] overflow-hidden'
      style={{
        background: `radial-gradient(ellipse 100% 60% at 50% 20%, rgba(255, 90, 31, 0.18) 0%, transparent 50%),
                     radial-gradient(ellipse 80% 50% at 50% 50%, rgba(196, 61, 16, 0.12) 0%, transparent 60%),
                     radial-gradient(ellipse 60% 40% at 100% 100%, rgba(255, 90, 31, 0.08) 0%, transparent 50%),
                     radial-gradient(ellipse 50% 30% at 0% 80%, rgba(196, 61, 16, 0.06) 0%, transparent 50%),
                     #F5F1E8`
      }}
    >

      {/*TODO: 76px - это высота header, вынести в константу*/}
      <div className='relative z-10 grid h-full' style={{ gridTemplateColumns: gridTemplate }}>
        {/* Left: Project sections sidebar */}
        <ProjectSidebar
          currentId={id}
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          hasCompleted={hasCompleted}
          activeSection={activeTab === 'artifacts' ? 'artifacts' : activeTab === 'inference' ? 'inference' : activeTab === 'monitoring' ? 'monitoring' : 'flow'}
          onOpenFlowInfo={() => {
            setInferenceMode(null)
            setActiveTab('flow')
            updateUrlParams(null, null)
          }}
          onOpenArtifacts={() => {
            setActiveTab('artifacts')
            setInferenceMode(null)
            updateUrlParams('artifacts', null)
          }}
          onOpenInference={() => {
            setActiveTab('inference')
            setInferenceMode(null)
            updateUrlParams('inference', null)
          }}
          onOpenMonitoring={() => {
            setInferenceMode(null)
            setActiveTab('monitoring')
            updateUrlParams('monitoring', null)
          }}
          onHelpClick={() => {
            setInferenceMode(null)
            setActiveTab('flow')
            setDataTab('file')
            setTourStep(0)
            setTourOpen(true)
          }}
        />
        {/* Center: Flow/Details/Timeline (50%) */}
        <div className='center-panel flex h-[calc(100vh-4.5rem)] min-w-0 flex-col border-x border-ink/10'>
          {activeTab === 'artifacts' ? (
            <div className='min-w-0 flex-1 overflow-hidden'>
              <ArtifactsView
                meta={projectMeta}
                workflowId={workflowId || projectMeta?.last_workflow_id}
                fallbackMetrics={(() => {
                  try {
                    const flat = live?.snapshot?.partials?.metrics
                    if (flat && Object.keys(flat).length) return flat
                  } catch {}
                  return {}
                })()}
                onDownloadModel={wid => {
                  try {
                    // Use workflow-based model download endpoint (proxied via Orchestrator -> Experiment-designer)
                    const url = `/api/workflow/${wid}/model`
                    window.open(url, '_blank')
                  } catch {}
                }}
              />
            </div>
          ) : activeTab === 'inference' ? (
            <div className='min-w-0 flex-1 overflow-hidden'>
              {inferenceMode === 'label' ? (
                <LabelDataView
                  onBack={() => { setInferenceMode(null); updateUrlParams('inference', null) }}
                  workflowId={workflowId || projectMeta?.last_workflow_id}
                  files={labelDataFiles}
                  setFiles={setLabelDataFiles}
                  result={labelDataResult}
                  setResult={setLabelDataResult}
                  status={labelDataStatus}
                  setStatus={setLabelDataStatus}
                  error={labelDataError}
                  setError={setLabelDataError}
                  progress={labelDataProgress}
                  setProgress={setLabelDataProgress}
                  workflowInfo={labelDataWorkflowInfo}
                  setWorkflowInfo={setLabelDataWorkflowInfo}
                />
              ) : inferenceMode ? (
                <InferenceView
                  mode={inferenceMode}
                  onBack={() => { setInferenceMode(null); updateUrlParams('inference', null) }}
                  workflowId={workflowId || projectMeta?.last_workflow_id}
                  tables={tables}
                  taskType={(() => {
                    try {
                      const reqs = live?.snapshot?.partials?.demand?.requirements
                      if (reqs?.task_type) return String(reqs.task_type).toLowerCase()
                      return live?.snapshot?.partials?.designer?.model_architecture?.task || null
                    } catch {
                      return null
                    }
                  })()}
                />
              ) : (
                <div className='flex h-full min-w-0 flex-col overflow-hidden'>
                  {/* Header */}
                  <div className='px-8 py-6 text-center' style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.16)' }}>
                    <h2 className='text-2xl font-medium tracking-tight text-ink'>{t('projectView.inference')}</h2>
                    <p className='mt-1 text-sm text-ink'>{t('projectView.inferenceDesc')}</p>
                  </div>

                  {/* Cards Grid */}
                  <div className='flex-1 overflow-y-auto px-8 py-8' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
                    <div className='grid gap-6 lg:grid-cols-2'>
                      {/* Card 1: Label Data */}
                      <div
                        className='group cursor-pointer rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1'
                        style={{ background: 'rgba(14, 14, 16, 0.02)', border: '1px solid rgba(14, 14, 16, 0.16)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.9)'; e.currentTarget.style.background = 'rgba(14, 14, 16, 0.03)' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.16)'; e.currentTarget.style.background = 'rgba(14, 14, 16, 0.02)' }}
                        onClick={() => { setInferenceMode('label'); updateUrlParams('inference', 'label') }}
                      >
                        <div className='mb-4 flex h-10 w-10 items-center justify-center rounded-xl' style={{ background: 'rgba(255, 90, 31, 0.1)' }}>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                            <polyline points='17,8 12,3 7,8' />
                            <line x1='12' y1='3' x2='12' y2='15' />
                          </svg>
                        </div>
                        <h3 className='mb-2 text-base font-medium text-ink'>{t('projectView.labelData')}</h3>
                        <p className='mb-6 text-sm leading-relaxed text-ink'>
                          {t('projectView.labelDataDesc')}
                        </p>
                        <button className='text-sm font-medium text-accent transition-colors hover:text-accent'>
                          {t('projectView.uploadData')}
                        </button>
                      </div>

                      {/* Card 2: Model Inference */}
                      <div
                        className='group cursor-pointer rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1'
                        style={{ background: 'rgba(14, 14, 16, 0.02)', border: '1px solid rgba(14, 14, 16, 0.16)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.9)'; e.currentTarget.style.background = 'rgba(14, 14, 16, 0.03)' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.16)'; e.currentTarget.style.background = 'rgba(14, 14, 16, 0.02)' }}
                        onClick={() => { setInferenceMode('batch'); updateUrlParams('inference', 'batch') }}
                      >
                        <div className='mb-4 flex h-10 w-10 items-center justify-center rounded-xl' style={{ background: 'rgba(196, 61, 16, 0.1)' }}>
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                            <path d='M13 2L3 14h9l-1 8 10-12h-9l1-8z' />
                          </svg>
                        </div>
                        <h3 className='mb-2 text-base font-medium text-ink'>{t('projectView.modelInference')}</h3>
                        <p className='mb-6 text-sm leading-relaxed text-ink'>
                          {t('projectView.modelInferenceDesc')}
                        </p>
                        <button className='text-sm font-medium text-accent transition-colors hover:text-accent'>
                          {t('projectView.startInference')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            !workflowId && (
              <div className='flex h-full flex-col'>
                {/* Header */}
                <div className='px-8 py-6 text-center' style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.16)' }}>
                  <h2 className='text-2xl font-medium tracking-tight text-ink'>{t('projectView.workflow')}</h2>
                  <p className='mt-1 text-sm text-ink'>{t('projectView.workflowDesc')}</p>
                </div>

                {/* Content */}
                <div className='flex-1 overflow-y-auto px-8 py-8' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
                  {/* Last Result Card */}
                  {projectMeta?.last_model && (
                    <div
                      className='mb-8 rounded-2xl p-6'
                      style={{ background: 'rgba(255, 90, 31, 0.05)', border: '1px solid rgba(255, 90, 31, 0.1)' }}
                    >
                      <div className='mb-4 flex items-center gap-3'>
                        <div className='flex h-8 w-8 items-center justify-center rounded-lg' style={{ background: 'rgba(255, 90, 31, 0.15)' }}>
                          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-accent'>
                            <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                            <polyline points='22,4 12,14.01 9,11.01' />
                          </svg>
                        </div>
                        <div>
                          <h3 className='text-sm font-medium text-ink'>{t('projectView.previousResult')}</h3>
                          <p className='text-xs text-ink'>{t('projectView.workflowPrefix')} {projectMeta?.last_model?.workflow_id || projectMeta?.last_workflow_id}</p>
                        </div>
                      </div>
                      {projectMeta?.last_model?.metrics && (
                        <div className='mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                          {Object.entries(projectMeta.last_model.metrics).slice(0, 4).map(([k, v]) => (
                            <div key={k} className='rounded-xl p-3' style={{ background: 'rgba(14, 14, 16, 0.03)' }}>
                              <div className='text-xs text-ink'>{k}</div>
                              <div className='mt-1 text-sm font-bold text-ink'>{String(v)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(projectMeta?.last_model?.workflow_id || projectMeta?.last_workflow_id) && (
                        <button
                          onClick={() => {
                            try {
                              const wid = projectMeta.last_model?.workflow_id || projectMeta.last_workflow_id
                              window.open(`/api/workflow/${wid}/model`, '_blank')
                            } catch {}
                          }}
                          className='text-sm font-medium text-accent transition-colors hover:text-accent'
                        >
                          {t('projectView.downloadModel')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Dataset Upload */}
                  <div className='mb-8'>
                    <label className='mb-3 block text-sm text-ink'>
                      {t('projectView.dataset')} <span className='text-accent'>*</span>
                    </label>
                    <div className="flex gap-4 mb-4 border-b border-ink/10">
                      <button
                        className={`pb-2 text-sm font-medium transition-colors ${dataTab === 'file' ? 'text-ink border-b-2 border-accent' : 'text-ink hover:text-ink'}`}
                        onClick={() => setDataTab('file')}
                      >Upload file</button>
                      <button
                        className={`pb-2 text-sm font-medium transition-colors ${dataTab === 'crm' ? 'text-ink border-b-2 border-accent' : 'text-ink hover:text-ink'}`}
                        onClick={() => setDataTab('crm')}
                      >Import from CRM</button>
                    </div>
                    {dataTab === 'file' ? (
                      <>
                        <div data-tour='upload'>
                          <ModInputFile
                            onChange={onUpload}
                            fileInfo={fileInfo}
                            multiple={true}
                            buttonText={t('projectView.chooseFiles')}
                            existingFiles={tables.map(t => ({ name: t.name, size: 0 }))}
                            onRemoveExisting={fileName => {
                              setTables(prev => prev.filter(t => t.name !== fileName))
                              processedFilesRef.current.forEach(key => {
                                if (key.startsWith(fileName + ':')) processedFilesRef.current.delete(key)
                              })
                            }}
                          />
                        </div>
                        {uploadError && (
                          <div className='mt-2 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600'>
                            <svg xmlns='http://www.w3.org/2000/svg' className='mt-0.5 h-4 w-4 shrink-0' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                              <circle cx='12' cy='12' r='10' />
                              <line x1='12' y1='8' x2='12' y2='12' />
                              <line x1='12' y1='16' x2='12.01' y2='16' />
                            </svg>
                            <span className='flex-1'>{uploadError}</span>
                            <button type='button' className='shrink-0 text-red-600/60 transition-colors hover:text-red-600' onClick={() => setUploadError('')} aria-label='Close'>
                              <svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                                <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                              </svg>
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <CrmImport
                        userId={project?.user_id}
                        onImport={(crmTables) => {
                          setTables(prev => {
                            let maxId = prev.length > 0 ? Math.max(...prev.map(t => (typeof t.id === 'number' ? t.id : 0)), -1) : -1
                            const newTables = crmTables.map(t => {
                              maxId += 1
                              return { ...t, id: maxId }
                            })
                            return [...prev, ...newTables]
                          })
                        }}
                      />
                    )}
                  </div>

                  {/* Target Column */}
                  <div className='mb-8'>
                    <label className='mb-3 block text-sm text-ink'>
                      {t('projectView.targetColumn')} <span className='text-accent'>*</span>
                    </label>
                    <div className='relative' data-tour='target'>
                      <select
                        value={target}
                        onChange={e => setTarget(e.target.value)}
                        className='mod-field w-full appearance-none rounded-xl px-4 py-3 pr-10 text-sm text-ink'
                      >
                        <option value='' className='bg-white text-ink'>{t('projectView.selectTarget')}</option>
                        {Array.isArray(tables) && tables.length > 0 &&
                          tables.flatMap(table =>
                            Array.isArray(table.columns)
                              ? table.columns.map(r => {
                                  const name = (r.name || '').trim()
                                  return name ? (
                                    <option key={name} value={name} className='bg-white text-ink'>{name}</option>
                                  ) : null
                                })
                              : []
                          ).filter(Boolean)
                        }
                      </select>
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        className='pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink'
                      >
                        <path d='m6 9 6 6 6-6' />
                      </svg>
                    </div>
                  </div>

                  {/* Column Editor */}
                  <div className='mb-8'>
                    {uploadingFiles.length > 0 ? (
                      <div
                        className='flex flex-col items-center justify-center rounded-2xl py-16'
                        style={{
                          background: 'rgba(255, 90, 31, 0.03)',
                          border: '1px solid rgba(255, 90, 31, 0.1)',
                        }}
                      >
                        {/* Animated loader */}
                        <div className='relative mb-6'>
                          <div
                            className='h-16 w-16 rounded-full'
                            style={{
                              background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.2), rgba(196, 61, 16, 0.2))',
                              animation: 'pulse 2s ease-in-out infinite',
                            }}
                          />
                          <div
                            className='absolute inset-2 rounded-full'
                            style={{
                              background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.4), rgba(196, 61, 16, 0.4))',
                              animation: 'pulse 2s ease-in-out infinite 0.3s',
                            }}
                          />
                          <div
                            className='absolute inset-4 flex items-center justify-center rounded-full'
                            style={{
                              background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.8), rgba(196, 61, 16, 0.8))',
                            }}
                          >
                            <svg
                              width='20'
                              height='20'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                              className='text-ink'
                              style={{ animation: 'spin 1.5s linear infinite' }}
                            >
                              <path d='M21 12a9 9 0 1 1-6.219-8.56' />
                            </svg>
                          </div>
                        </div>
                        <h3 className='mb-2 text-base font-medium text-ink'>
                          {t('projectView.checkingUploading')}
                        </h3>
                        <p className='mb-4 text-sm text-ink'>
                          {uploadingFiles.length === 1
                            ? t('projectView.uploadingFile', { name: uploadingFiles[0].name })
                            : t('projectView.uploadingFiles', { count: uploadingFiles.length })}
                        </p>
                        {/* File list with progress */}
                        <div className='flex flex-col gap-3 w-full max-w-md'>
                          {uploadingFiles.map((file, idx) => {
                            const progress = uploadProgress[file.name] || 0
                            return (
                              <div key={idx} className='w-full'>
                                <div className='flex justify-between text-xs mb-1'>
                                  <span className='text-ink truncate max-w-[200px]'>{file.name}</span>
                                  <span className='text-accent'>{progress}%</span>
                                </div>
                                <div
                                  className='h-2 rounded-full overflow-hidden'
                                  style={{ background: 'rgba(255, 90, 31, 0.1)' }}
                                >
                                  <div
                                    className='h-full rounded-full transition-all duration-300'
                                    style={{
                                      width: `${progress}%`,
                                      background: 'linear-gradient(90deg, #8B5CF6, #A78BFA)',
                                    }}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ) : tables.length > 1 ? (
                      <TableEditor
                        tables={tables}
                        selectedTableId={selectedTableId}
                        onTableSelect={setSelectedTableId}
                        onTablesChange={setTables}
                        relations={relations}
                        onRelationsChange={setRelations}
                      />
                    ) : (
                      <ColumnEditor
                        value={tables.length > 0 && tables[0] ? tables[0].columns : []}
                        onChange={newColumns => {
                          if (tables.length > 0) {
                            const updated = [{ ...tables[0], columns: newColumns }]
                            setTables(updated)
                          } else {
                            const updated = [{ id: 0, name: 'dataset.csv', columns: newColumns }]
                            setTables(updated)
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div
                    className='mb-8 flex items-start gap-3 rounded-xl p-4'
                    style={{ background: 'rgba(255, 90, 31, 0.05)', border: '1px solid rgba(255, 90, 31, 0.1)' }}
                  >
                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='mt-0.5 flex-shrink-0 text-accent'>
                      <circle cx='12' cy='12' r='10' />
                      <line x1='12' y1='16' x2='12' y2='12' />
                      <line x1='12' y1='8' x2='12.01' y2='8' />
                    </svg>
                    <p className='text-sm leading-relaxed text-ink'>
                      {t('projectView.infoTip')}
                    </p>
                  </div>

                  {/* Submit */}
                  <div className='flex justify-center'>
                    <div
                      className='relative inline-block'
                      onMouseEnter={() => {
                        const el = launchBtnRef.current
                        if (el) setLaunchHintRect(el.getBoundingClientRect())
                        setLaunchHintOpen(true)
                      }}
                      onMouseLeave={() => setLaunchHintOpen(false)}
                    >
                      <button
                        ref={launchBtnRef}
                        data-tour='launch'
                        onClick={onSendAndStart}
                        disabled={tables.length === 0 || tables.every(t => !t.dataset_path) || !target || !text.trim() || startPending}
                        className='flex items-center gap-2 rounded-xl px-8 py-3 text-sm font-medium text-ink transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40'
                        style={{ background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.8), rgba(196, 61, 16, 0.8))' }}
                      >
                        {savingMeta || startPending ? (
                          <>
                            <span className='loading loading-spinner loading-sm'></span>
                            {t('projectView.processingBtn')}
                          </>
                        ) : (
                          <>
                            {t('projectView.startWorkflow')}
                            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                              <path d='m9 18 6-6-6-6' />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
          {/* Monitoring Tab - Full Screen */}
          {activeTab === 'monitoring' && hasCompleted && (
            <div className='flex h-full flex-col overflow-y-auto' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
              <MonitoringWrapper
                workflowId={workflowId || projectMeta?.last_workflow_id}
                taskType={(() => {
                  try {
                    return live?.snapshot?.partials?.designer?.model_architecture?.task || 'classification'
                  } catch {
                    return 'classification'
                  }
                })()}
                initialView={(() => { try { return new URLSearchParams(window.location.search).get('view') } catch { return null } })()}
                onViewChange={(view) => updateUrlParams('monitoring', view)}
              />
            </div>
          )}

          {workflowId && activeTab !== 'artifacts' && activeTab !== 'inference' && activeTab !== 'monitoring' && (
            <div className='flex flex-1 flex-col'>
              {/* Workflow Header with Progress */}
              <div className='px-8 py-6' style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.16)' }}>
                <div className='flex items-center justify-between'>
                  <div className='flex-1 text-center'>
                    <h2 className='text-2xl font-medium tracking-tight text-ink'>{t('projectView.workflowProgress')}</h2>
                    <div className='mt-2 flex items-center justify-center gap-3'>
                      <div className='flex items-center gap-2'>
                        <span
                          className={`h-2 w-2 rounded-full ${live?.status?.done ? 'bg-accent/15' : 'bg-accent/15 animate-pulse'}`}
                        />
                        <span className='text-sm text-ink'>
                          {live?.status?.done ? t('projectView.completed') : live?.status?.status || t('projectView.running')}
                        </span>
                      </div>
                      <span className='text-sm text-ink'>•</span>
                      <span className='font-mono text-xs text-ink'>{workflowId.slice(0, 12)}</span>
                    </div>
                  </div>
                  {/* Time Estimates */}
                  {!live?.status?.done && live?.snapshot?.run_meta?.started_at && (
                    <div className='text-right'>
                      <div className='text-xs text-ink'>{t('projectView.elapsed')}</div>
                      <div className='text-sm font-medium text-ink'>
                        {(() => {
                          try {
                            const raw = live.snapshot.run_meta.started_at
                            const started = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z')
                            const elapsed = Math.floor((Date.now() - started.getTime()) / 1000)
                            const mins = Math.floor(elapsed / 60)
                            const secs = elapsed % 60
                            return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
                          } catch {
                            return '--'
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Progress Bar - hide when Human Review is active */}
                {!live?.snapshot?.pending_human_request && (() => {
                  const steps = ['demand', 'analyst', 'designer', 'validator']
                  const partials = live?.snapshot?.partials || {}
                  let completedCount = 0
                  let currentStepIndex = -1

                  steps.forEach((step, idx) => {
                    const stepData = partials[step]
                    if (stepData?.execution_summary?.status === 'completed' || stepData?.execution_summary?.completed_at) {
                      completedCount++
                    } else if (live?.currentStep === step) {
                      currentStepIndex = idx
                    }
                  })

                  if (live?.status?.done) completedCount = steps.length

                  const progress = Math.round((completedCount / steps.length) * 100)

                  const stepLabels = {
                    demand: t('stepDetails.demand'),
                    analyst: t('stepDetails.analyst'),
                    designer: t('stepDetails.designer'),
                    validator: t('stepDetails.validator'),
                  }

                  return (
                    <div className='mt-5'>
                      {/* Step Labels */}
                      <div className='mb-3 flex justify-between gap-2'>
                        {steps.map((step, idx) => {
                          const stepData = partials[step]
                          const isCompleted = stepData?.execution_summary?.status === 'completed' || stepData?.execution_summary?.completed_at || live?.status?.done
                          const isCurrent = live?.currentStep === step && !live?.status?.done
                          return (
                            <div
                              key={step}
                              className='flex flex-1 flex-col items-center gap-2 rounded-lg px-3 py-3.5 transition-all'
                              style={{
                                background: 'rgba(255, 255, 255, 0.4)',
                                border: `1px solid ${
                                  isCompleted
                                    ? 'var(--accent)'
                                    : isCurrent
                                    ? 'var(--accent)'
                                    : 'rgba(14, 14, 16, 0.16)'
                                }`,
                                boxShadow: (isCompleted || isCurrent)
                                  ? '0 6px 16px -12px rgba(255, 90, 31, 0.3)'
                                  : 'none',
                              }}
                            >
                              {/* Status Icon */}
                              <div
                                className='flex h-7 w-7 items-center justify-center rounded-md transition-all'
                                style={{
                                  background: isCompleted
                                    ? 'var(--accent)'
                                    : isCurrent
                                    ? 'rgba(255, 90, 31, 0.18)'
                                    : 'rgba(14, 14, 16, 0.06)',
                                  border: isCompleted
                                    ? '1px solid var(--accent)'
                                    : isCurrent
                                    ? '1px solid rgba(255, 90, 31, 0.45)'
                                    : '1px solid rgba(14, 14, 16, 0.16)',
                                }}
                              >
                                {isCompleted ? (
                                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' style={{ color: '#fff' }}>
                                    <path d='M20 6L9 17l-5-5' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
                                  </svg>
                                ) : isCurrent ? (
                                  <div className='relative flex h-3 w-3 items-center justify-center'>
                                    <div className='absolute h-full w-full animate-ping rounded-full' style={{ background: 'var(--accent)', opacity: 0.45 }} />
                                    <div className='h-2 w-2 rounded-full' style={{ background: 'var(--accent)' }} />
                                  </div>
                                ) : (
                                  <div className='h-1.5 w-1.5 rounded-full' style={{ background: 'var(--accent)', opacity: 0.6 }} />
                                )}
                              </div>
                              <span
                                className='text-[10px] font-semibold uppercase tracking-wider'
                                style={{ color: isCompleted || isCurrent ? 'var(--accent)' : 'var(--muted)' }}
                              >
                                {stepLabels[step] || step}
                              </span>
                            </div>
                          )
                        })}
                      </div>

                      {/* Progress Track */}
                      <div className='relative h-1.5 rounded-full' style={{ background: 'rgba(14, 14, 16, 0.10)' }}>
                        <div
                          className='absolute left-0 top-0 h-full rounded-full transition-all duration-500'
                          style={{
                            width: `${progress}%`,
                            background: live?.status?.done
                              ? 'linear-gradient(90deg, rgba(255, 90, 31, 0.8), rgba(255, 90, 31, 0.6))'
                              : 'linear-gradient(90deg, rgba(255, 90, 31, 0.8), rgba(196, 61, 16, 0.6))',
                            boxShadow: live?.status?.done
                              ? '0 0 8px rgba(255, 90, 31, 0.4)'
                              : '0 0 8px rgba(255, 90, 31, 0.4)',
                          }}
                        />
                        {/* Step Markers */}
                        {steps.map((step, idx) => {
                          const position = ((idx + 1) / steps.length) * 100
                          const stepData = partials[step]
                          const isCompleted = stepData?.execution_summary?.status === 'completed' || stepData?.execution_summary?.completed_at || live?.status?.done
                          const isCurrent = live?.currentStep === step && !live?.status?.done
                          return (
                            <div
                              key={step}
                              className='absolute top-1/2 -translate-x-1/2 -translate-y-1/2'
                              style={{ left: `${position}%` }}
                            >
                              <div
                                className={`h-3 w-3 rounded-full border-2 transition-all ${
                                  isCompleted
                                    ? 'border-accent'
                                    : isCurrent
                                    ? 'border-accent animate-pulse'
                                    : ''
                                }`}
                                style={{
                                  background: isCompleted ? 'var(--accent)' : 'transparent',
                                  borderColor: isCompleted || isCurrent ? 'var(--accent)' : 'rgba(14, 14, 16, 0.35)',
                                  backgroundColor: isCurrent ? 'rgba(255, 90, 31, 0.25)' : isCompleted ? 'var(--accent)' : 'transparent',
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>

                      {/* Progress Text */}
                      <div className='mt-2 flex items-center justify-between'>
                        <span className='text-xs text-ink'>
                          {t('projectView.stepOf', { current: Math.min(completedCount + 1, steps.length), total: steps.length })}
                        </span>
                        <span className='text-xs font-medium text-ink'>{progress}%</span>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Tabs - hide when Human Review is active */}
              {!live?.snapshot?.pending_human_request && (
                <div className='px-8 pt-5 flex justify-center'>
                  <div className='inline-flex gap-1 p-1 rounded-full' style={{ background: 'rgba(14, 14, 16, 0.05)', border: '1px solid var(--hairline-strong)' }}>
                    {[
                      { id: 'flow', label: t('projectView.flow') },
                      { id: 'details', label: t('projectView.details') }
                    ].map(tab => {
                      const isActive = activeTab === tab.id
                      return (
                        <button
                          key={tab.id}
                          onClick={() => { setActiveTab(tab.id); updateUrlParams(null, tab.id === 'details' ? 'details' : null) }}
                          className='px-5 py-1.5 text-[13px] font-medium rounded-full transition-all'
                          style={{
                            background: isActive ? 'var(--ink)' : 'transparent',
                            color: isActive ? 'var(--cream)' : 'var(--muted)',
                            boxShadow: isActive ? '0 6px 16px -8px rgba(14, 14, 16, 0.35)' : 'none',
                            minWidth: 110,
                          }}
                        >
                          {tab.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Content */}
              <div className='flex-1 overflow-auto px-8 py-6' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
                {/* Human review card when there is a pending request */}
                {live?.snapshot?.pending_human_request && (
                  <HumanReviewCard
                    request={live.snapshot.pending_human_request}
                    busy={resolvingHR}
                    initialTables={live?.snapshot?.input?.tables || []}
                    initialColumnsSchema={
                      live?.snapshot?.input?.tables?.[0]?.columns || live?.snapshot?.input?.columns_schema || {}
                    }
                    initialTarget={(() => {
                      try {
                        return live?.snapshot?.partials?.demand?.requirements?.target || ''
                      } catch {
                        return ''
                      }
                    })()}
                    onSubmit={async payload => {
                      try {
                        setResolvingHR(true)
                        await humanResolve(workflowId, payload)
                        try {
                          await live?.refresh?.()
                        } catch {}
                        const m = await sendMessage(id, 'system', t('projectView.humanReviewResolved'))
                        setMessages(prev => [...prev, m])
                      } catch (e) {
                        console.error(e)
                      } finally {
                        setResolvingHR(false)
                      }
                    }}
                  />
                )}

                {/* Flow Tab Content */}
                {activeTab === 'flow' && !live?.snapshot?.pending_human_request && (
                  <>
                    {live.loading ? (
                      live.showLoading ? (
                        <div className='flex h-40 items-center justify-center'>
                          <span className='loading loading-spinner loading-md text-accent' />
                        </div>
                      ) : null
                    ) : (
                      <div className='space-y-4'>
                        <div className='flex justify-end'>
                          <button
                            onClick={() => setGraphOpen(true)}
                            className='rounded-lg px-4 py-2 text-sm text-ink transition-all hover:bg-ink/[0.05] hover:text-ink'
                            style={{ border: '1px solid rgba(14, 14, 16, 0.16)' }}
                          >
                            {t('projectView.expandView')}
                          </button>
                        </div>
                        <FlowGraph
                          nodes={live.nodes || []}
                          edges={live.edges || []}
                          currentStep={live.currentStep}
                          onSelect={setSelectedStep}
                          summaries={flowSummaries}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Details Tab Content */}
                {activeTab === 'details' && !live?.snapshot?.pending_human_request && (
                  <StepDetails
                    step={selectedStep}
                    snapshot={live.snapshot}
                    status={live.status}
                    onDownload={wid => {
                      try {
                        window.open(`/api/workflow/${wid}/model`, '_blank')
                      } catch {}
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {/* Right: Chat (30%) — collapsible */}
        {chatCollapsed ? (
          <div
            className={`flex h-[calc(100vh-4.5rem)] flex-col items-center py-5 ${activeTab === 'artifacts' ? 'hidden' : ''}`}
            style={{ background: 'var(--cream)', borderLeft: '1px solid var(--hairline-strong)' }}
          >
            <button
              type='button'
              onClick={toggleChat}
              aria-label={t('projectView.expandChat') || 'Expand chat'}
              title={t('projectView.expandChat') || 'Expand chat'}
              className='flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-ink/[0.06]'
              style={{ color: 'var(--muted)' }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='m15 18-6-6 6-6' />
              </svg>
            </button>
            <div
              className='mt-3 flex h-9 w-9 items-center justify-center rounded-md'
              style={{ background: 'rgba(255, 90, 31, 0.1)', color: 'var(--accent)' }}
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
              </svg>
            </div>
          </div>
        ) : (
        <div
          className={`flex h-[calc(100vh-4.5rem)] min-w-0 flex-col ${activeTab === 'artifacts' ? 'hidden' : ''}`}
          style={{ background: 'var(--cream)', borderLeft: '1px solid var(--hairline-strong)' }}
        >
          {/* Chat Header */}
          <div
            className='flex items-center justify-between px-6 py-4'
            style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.16)' }}
          >
            <div className='min-w-0'>
              <h3 className='text-sm font-medium text-ink truncate'>{project?.name || t('projectView.chatFallback')}</h3>
              <p className='mt-0.5 text-xs text-ink'>{t('projectView.chatDesc')}</p>
            </div>
            <button
              type='button'
              onClick={toggleChat}
              aria-label={t('projectView.collapseChat') || 'Collapse chat'}
              title={t('projectView.collapseChat') || 'Collapse chat'}
              className='ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors hover:bg-ink/[0.06]'
              style={{ color: 'var(--muted)' }}
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='m9 18 6-6-6-6' />
              </svg>
            </button>
          </div>

          {/* Messages Area */}
          <div className='flex-1 overflow-y-auto px-5 py-6' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
            {messages.length === 0 ? (
              <div className='flex h-full flex-col items-center justify-center text-center'>
                <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-2xl' style={{ background: 'rgba(255, 90, 31, 0.1)' }}>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                    <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
                  </svg>
                </div>
                <h4 className='text-sm font-medium text-ink'>{t('projectView.startConversation')}</h4>
                <p className='mt-1 max-w-[200px] text-xs text-ink'>
                  {t('projectView.startConversationDesc')}
                </p>
              </div>
            ) : (
              <div className='space-y-5'>
                {messages.map((m, index) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    style={{ animation: 'fadeIn 0.2s ease-out', animationDelay: `${index * 0.03}s`, animationFillMode: 'both' }}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'rounded-tr-md bg-accent/15 text-ink'
                          : 'rounded-tl-md text-ink'
                      }`}
                      style={m.role !== 'user' ? { background: 'rgba(14, 14, 16, 0.04)' } : {}}
                    >
                      <span dangerouslySetInnerHTML={{ __html: m.content
                        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.+?)\*/g, '<em>$1</em>')
                        .replace(/\n/g, '<br/>')
                      }} />
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className='flex justify-start' style={{ animation: 'fadeIn 0.2s ease-out' }}>
                    <div className='rounded-2xl rounded-tl-md px-4 py-3' style={{ background: 'rgba(14, 14, 16, 0.04)' }}>
                      <div className='flex items-center gap-1'>
                        <span className='h-1.5 w-1.5 rounded-full bg-ink/30' style={{ animation: 'chatDot 1.2s ease-in-out infinite' }} />
                        <span className='h-1.5 w-1.5 rounded-full bg-ink/30' style={{ animation: 'chatDot 1.2s ease-in-out 0.2s infinite' }} />
                        <span className='h-1.5 w-1.5 rounded-full bg-ink/30' style={{ animation: 'chatDot 1.2s ease-in-out 0.4s infinite' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input Area */}
          <div className='px-5 pb-5 pt-3'>
            <div
              className='relative rounded-2xl p-[1px]'
              style={{
                background: launchHintOpen && !text.trim() && !workflowId
                  ? 'linear-gradient(180deg, rgba(255, 132, 83, 0.55) 0%, rgba(255, 90, 31, 0.3) 100%)'
                  : 'linear-gradient(180deg, rgba(14, 14, 16, 0.11) 0%, rgba(14, 14, 16, 0.04) 100%)',
                boxShadow: launchHintOpen && !text.trim() && !workflowId
                  ? '0 -8px 40px rgba(255, 90, 31, 0.55), 0 -4px 18px rgba(255, 132, 83, 0.3), 0 0 0 1px rgba(255, 132, 83, 0.25)'
                  : '0 -8px 32px rgba(255, 90, 31, 0.15), 0 -4px 16px rgba(14, 14, 16, 0.05)',
                transition: 'box-shadow 0.35s ease, background 0.35s ease',
              }}
            >
              <div
                className='relative rounded-[15px]'
                style={{
                  background: '#ffffff',
                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                }}
              >
                <textarea
                  data-tour='description'
                  placeholder={workflowId ? t('projectView.askPlaceholder') : t('projectView.describePlaceholder')}
                  value={text}
                  onChange={e => {
                    setText(e.target.value)
                    // Auto-resize
                    e.target.style.height = 'auto'
                    const maxH = window.innerHeight * 0.4
                    e.target.style.height = Math.min(e.target.scrollHeight, maxH) + 'px'
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      if (!workflowId) {
                        const canStart =
                          tables.length > 0 &&
                          !tables.every(t => !t.dataset_path) &&
                          target &&
                          text.trim() &&
                          !startPending
                        if (canStart) onSendAndStart()
                        return
                      }
                      onSend()
                    }
                  }}
                  rows={1}
                  className='w-full resize-none bg-transparent px-4 py-3.5 pr-12 text-sm text-ink placeholder-ink/40 outline-none'
                  style={{ minHeight: '48px', maxHeight: '40vh', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}
                />
                {/* Send Button — only after workflow exists; before that, the textarea is the task description */}
                {text.trim() && workflowId && (
                  <button
                    onClick={onSend}
                    className='absolute bottom-2.5 right-2.5 flex h-8 w-8 items-center justify-center rounded-lg transition-all hover:bg-accent/15'
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 90, 31, 0.9), rgba(196, 61, 16, 0.9))',
                      boxShadow: '0 2px 8px rgba(255, 90, 31, 0.3)',
                    }}
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' className='text-ink'>
                      <path d='M12 19V5M5 12l7-7 7 7' />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
      {graphOpen && (
        <div
          className='fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-md animate-fade-in'
          onClick={() => setGraphOpen(false)}
        >
          <div
            className='premium-card flex h-[85vh] w-[90vw] max-w-6xl animate-scale-in flex-col'
            onClick={e => e.stopPropagation()}
          >
            <div className='premium-card-border' />
            <div className='flex flex-1 flex-col overflow-hidden'>
              <div className='relative flex items-center justify-center px-6 py-4' style={{ borderBottom: '1px solid var(--hairline-strong)' }}>
                <div className='text-center'>
                  <div className='text-lg font-medium tracking-tight text-ink'>{t('projectView.workflowFlow')}</div>
                  <div className='mt-0.5 text-xs text-muted'>{t('projectView.workflowFlowDesc')}</div>
                </div>
                <button
                  type='button'
                  onClick={() => setGraphOpen(false)}
                  aria-label={t('projectView.close')}
                  className='absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-full transition-colors hover:bg-ink/[0.06]'
                  style={{ width: 32, height: 32, color: 'var(--muted)' }}
                >
                  <svg width='16' height='16' viewBox='0 0 20 20' fill='none' stroke='currentColor' strokeWidth='1.5'>
                    <path d='M5 5l10 10M15 5l-10 10' strokeLinecap='round' />
                  </svg>
                </button>
              </div>
              <div className='flex-1 overflow-auto p-6'>
                {live.loading ? (
                  live.showLoading ? (
                    <div className='flex h-full items-center justify-center'>
                      <div className='text-center'>
                        <span className='loading loading-spinner loading-lg text-accent'></span>
                        <div className='mt-4 text-sm text-ink'>{t('projectView.loadingWorkflow')}</div>
                      </div>
                    </div>
                  ) : null
                ) : (
                  <FlowGraph
                    nodes={live.nodes || []}
                    edges={live.edges || []}
                    currentStep={live.currentStep}
                    onSelect={setSelectedStep}
                    summaries={flowSummaries}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {inspectOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md animate-fade-in' onClick={() => {}}>
          <div
            className='analysis-card animate-scale-in'
            onClick={e => e.stopPropagation()}
          >
            <div className='analysis-card-content'>
              <div className='analysis-ring-wrap'>
                <div className='analysis-ring-bg' />
                <svg className='analysis-ring-svg' viewBox='0 0 140 140' aria-hidden='true'>
                  <defs>
                    <linearGradient id='analysisRingGrad' x1='0%' y1='0%' x2='100%' y2='100%'>
                      <stop offset='0%' stopColor='#FF8453' />
                      <stop offset='50%' stopColor='#FF5A1F' />
                      <stop offset='100%' stopColor='#C43D10' />
                    </linearGradient>
                  </defs>
                  <circle className='analysis-ring-track' cx='70' cy='70' r='60' />
                  <circle
                    className='analysis-ring-progress'
                    cx='70'
                    cy='70'
                    r='60'
                    style={{
                      strokeDasharray: 377,
                      strokeDashoffset: 377 - (analysisProgress / 100) * 377,
                    }}
                  />
                </svg>
                <div className={`analysis-ring-center ${!inspectLoading && inspectReady ? 'analysis-ring-center--hidden' : ''}`}>
                  <div className='analysis-ring-pct'>
                    <span>{Math.round(analysisProgress)}</span>
                    <span className='analysis-ring-pct-sign'>%</span>
                  </div>
                  <div className='analysis-ring-label'>{t('projectView.analyzingLabel')}</div>
                </div>
                <div className={`analysis-ring-check ${!inspectLoading && inspectReady ? 'analysis-ring-check--show' : ''}`}>
                  <svg width='56' height='56' viewBox='0 0 24 24' fill='none' stroke='#FF5A1F' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                    <circle cx='12' cy='12' r='10' stroke='rgba(255, 90, 31, 0.35)' />
                    <polyline points='8 12 11 15 16 9' />
                  </svg>
                </div>
              </div>

              <div className='analysis-modal-title'>{t('projectView.datasetAnalysis')}</div>
              <div className='analysis-modal-subtitle'>{t('projectView.detectingColumns')}</div>

              <div className={`analysis-modal-status ${!inspectLoading && inspectReady ? 'analysis-modal-status--complete' : ''}`}>
                {!inspectLoading && inspectReady ? (
                  <>
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                      <polyline points='20 6 9 17 4 12' />
                    </svg>
                    <span>{t('projectView.analysisComplete')}</span>
                  </>
                ) : (
                  <span>
                    {analysisProgress < 22
                      ? t('projectView.analysisStageReading')
                      : analysisProgress < 55
                        ? t('projectView.analysisStageParsing')
                        : analysisProgress < 85
                          ? t('projectView.analysisStageDetecting')
                          : t('projectView.analysisStageFinalizing')}
                  </span>
                )}
              </div>

              <button
                type='button'
                className='analysis-btn-purple'
                disabled={!inspectReady}
                onClick={() => {
                  if (!inspectReady) return
                  setInspectOpen(false)
                  setInspectReady(false)
                }}
              >
                {t('projectView.continueBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {launchHintOpen && !text.trim() && launchHintRect && createPortal(
        <div
          className='launch-hint launch-hint--open'
          role='tooltip'
          style={{
            position: 'fixed',
            left: launchHintRect.left + launchHintRect.width / 2,
            top: launchHintRect.top - 14,
            transform: 'translate(-50%, -100%)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <div className='launch-hint__body'>
            <span className='launch-hint__icon'>
              <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
            </span>
            <span className='launch-hint__text'>{t('projectView.launchHintText')}</span>
          </div>
        </div>,
        document.body
      )}
      {tourOpen && tourRect && createPortal(
        (() => {
          const steps = [
            { key: 'upload',      title: t('projectView.tourStep1Title'),      text: t('projectView.tourStep1Text') },
            { key: 'target',      title: t('projectView.tourStep2Title'),      text: t('projectView.tourStep2Text') },
            { key: 'description', title: t('projectView.tourStep3Title'),      text: t('projectView.tourStep3Text') },
            { key: 'launch',      title: t('projectView.tourStep4Title'),      text: t('projectView.tourStep4Text') },
          ]
          const vw = window.innerWidth
          const vh = window.innerHeight
          const pad = 8
          const halo = {
            left: tourRect.left - pad,
            top: tourRect.top - pad,
            width: tourRect.width + pad * 2,
            height: tourRect.height + pad * 2,
          }
          const cardW = 280, cardH = 172, gap = 22
          // choose side: prefer right, fall back to left, then below, then above
          let cardPos
          const rightSpace = vw - (tourRect.left + tourRect.width) - gap
          const leftSpace = tourRect.left - gap
          if (rightSpace >= cardW + 12) cardPos = 'right'
          else if (leftSpace >= cardW + 12) cardPos = 'left'
          else if (vh - (tourRect.top + tourRect.height) >= cardH + gap) cardPos = 'below'
          else cardPos = 'above'

          let cx, cy
          if (cardPos === 'right') { cx = tourRect.left + tourRect.width + gap; cy = tourRect.top + tourRect.height / 2 - cardH / 2 }
          else if (cardPos === 'left') { cx = tourRect.left - cardW - gap; cy = tourRect.top + tourRect.height / 2 - cardH / 2 }
          else if (cardPos === 'below') { cx = tourRect.left + tourRect.width / 2 - cardW / 2; cy = tourRect.top + tourRect.height + gap }
          else { cx = tourRect.left + tourRect.width / 2 - cardW / 2; cy = tourRect.top - cardH - gap }
          cx = Math.max(10, Math.min(vw - cardW - 10, cx))
          cy = Math.max(10, Math.min(vh - cardH - 10, cy))

          // Arrow: from card edge closest to target, to target center
          const targetCx = tourRect.left + tourRect.width / 2
          const targetCy = tourRect.top + tourRect.height / 2
          let sx, sy
          if (cardPos === 'right') { sx = cx;            sy = cy + cardH / 2 }
          else if (cardPos === 'left')  { sx = cx + cardW;    sy = cy + cardH / 2 }
          else if (cardPos === 'below') { sx = cx + cardW / 2; sy = cy }
          else                          { sx = cx + cardW / 2; sy = cy + cardH }
          const dx = targetCx - sx, dy = targetCy - sy
          const c1x = sx + dx * 0.2, c1y = sy + dy * 0.6
          const c2x = sx + dx * 0.6, c2y = sy + dy * 0.3
          const d = `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${targetCx} ${targetCy}`
          const isLast = tourStep === steps.length - 1

          const isVirtual = !!tourRect._virtual
          const cardFinalX = isVirtual ? (vw / 2 - cardW / 2) : cx
          const cardFinalY = isVirtual ? (vh / 2 - cardH / 2) : cy
          return (
            <>
              {!isVirtual && (
                <div className='pv-tour-halo' style={{ position: 'fixed', left: halo.left, top: halo.top, width: halo.width, height: halo.height, zIndex: 1001, pointerEvents: 'none' }} />
              )}
              {!isVirtual && (
                <svg className='pv-tour-arrow-svg' style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', zIndex: 1002, pointerEvents: 'none' }}>
                  <defs>
                    <linearGradient id='pvTourGrad' x1='0%' y1='0%' x2='100%' y2='0%'>
                      <stop offset='0%' stopColor='#FF8453' />
                      <stop offset='100%' stopColor='#C43D10' />
                    </linearGradient>
                  </defs>
                  <path className='pv-tour-arrow' d={d} />
                </svg>
              )}
              {isVirtual && (
                <div
                  style={{ position: 'fixed', inset: 0, background: 'rgba(14, 14, 16, 0.32)', backdropFilter: 'blur(2px)', zIndex: 1001, pointerEvents: 'none' }}
                />
              )}
              <div
                className='pv-tour-card'
                style={{ position: 'fixed', left: cardFinalX, top: cardFinalY, width: cardW, zIndex: 1003 }}
                role='dialog'
                aria-label='Tour step'
              >
                <div className='pv-tour-card__head'>
                  <span className='pv-tour-card__step'>{t('projectView.tourStepLabel', { current: tourStep + 1, total: steps.length })}</span>
                  <span className='pv-tour-card__title'>{steps[tourStep].title}</span>
                </div>
                <div className='pv-tour-dots'>
                  {steps.map((_, i) => (
                    <span key={i} className={i <= tourStep ? 'on' : ''} />
                  ))}
                </div>
                <div className='pv-tour-card__text'>{steps[tourStep].text}</div>
                <div className='pv-tour-card__actions'>
                  <button type='button' className='pv-tour-skip' onClick={() => setTourOpen(false)}>{t('projectView.tourSkip')}</button>
                  <div className='pv-tour-nav'>
                    <button
                      type='button'
                      className='pv-tour-btn'
                      disabled={tourStep === 0}
                      onClick={() => setTourStep(s => Math.max(0, s - 1))}
                    >
                      {t('projectView.tourBack')}
                    </button>
                    <button
                      type='button'
                      className='pv-tour-btn pv-tour-btn--primary'
                      onClick={() => {
                        if (isLast) setTourOpen(false)
                        else setTourStep(s => s + 1)
                      }}
                    >
                      {isLast ? t('projectView.tourFinish') : t('projectView.tourNext')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )
        })(),
        document.body
      )}
    </div>
  )
}
